import { FC, useContext, useEffect } from 'react';
import { BasicAlert, CollapsiblePanel, ICollapsiblePanelComponentProps, Markdown } from '@ngt/forms';
import { alpha, Box, Button, Chip, Divider, styled, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRepeat, faTableList } from '@fortawesome/pro-duotone-svg-icons';
import { ISourceDataFormCollectionComponentProps } from '@/utilities/forms/SourceDataFormProcessor';
import SourceDataExpandingContext from '@/contexts/forms/SourceDataExpandingContext';
import useSourceDataSectionReviewCount from '@/hooks/form/useSourceDataSectionReviewCount';
import { faCheckCircle, faExclamationCircle, faExclamationTriangle, faInfoCircle } from '@fortawesome/pro-light-svg-icons';

const CollapsiblePanelTitle = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    justifyContent: 'flex-start',
    borderRadius: 0,
    minHeight: 74
}));

const IconContainer = styled(Box)(({ theme }) => ({
    marginRight: theme.spacing(2),
    display: 'inline-block'
}));

const TitleChip = styled(Chip)(({ theme }) => ({
    marginLeft: 'auto'
}));

const RepeatChip = styled(Chip)(({ auto, theme }: any) => ({
    marginLeft: auto ? 'auto' : theme.spacing(1)
}));

const Content = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(16)
}));

const CountChip = styled(Chip)(({ auto, theme }: any) => ({
    marginLeft: auto ? 'auto' : theme.spacing(1),
    minWidth: 110
}));

const CountFlex = styled(Box)(({ auto, theme }: any) => ({
    display: 'flex'
}));

const Count = styled(Box)(({ auto, theme }: any) => ({
    minWidth: 50
}));

const SourceDataFormCollectionExpanding: FC<ISourceDataFormCollectionComponentProps> = ({
    formSectionLabel,
    formSectionRepeat,
    formSectionIdentifier,
    formSectionConfiguration,
    formPropertyPath,
    fieldKeys,
    settings,
    children
}) => {

    const { expanded, toggleExpanding } = useContext(SourceDataExpandingContext);

    const counts = useSourceDataSectionReviewCount(formPropertyPath!, fieldKeys, formSectionRepeat!);

    return (
        <Content>
            <CollapsiblePanelTitle 
                variant='contained' 
                color="secondary"
                fullWidth
                onClick={() => toggleExpanding(formSectionIdentifier!)}
            >
                <Typography variant="h4">
                    <IconContainer>
                        <FontAwesomeIcon icon={faTableList} />
                    </IconContainer>
                    {formSectionLabel}
                </Typography>
                {
                    formSectionRepeat && (
                        <Tooltip title="Repeat">
                            <RepeatChip
                                // @ts-ignore
                                auto="1"
                                icon={<FontAwesomeIcon icon={faRepeat} />}
                                label={formSectionRepeat}
                            />
                        </Tooltip>
                    )
                }
                {
                    counts && formSectionRepeat && (
                        <Tooltip title="Fields Pending Review">
                            <CountChip
                                // @ts-ignore
                                auto={!formSectionRepeat ? "1" : undefined}
                                label={
                                    <CountFlex>
                                        <Count>
                                            {counts.reviewed}
                                        </Count>
                                        /
                                        <Count>
                                            {counts.total}
                                        </Count>
                                    </CountFlex>
                                }
                                color={counts.reviewed !== counts.total ? 'error' : 'success'}
                            />
                        </Tooltip>
                    )
                }
            </CollapsiblePanelTitle>
            {
                expanded[formSectionIdentifier!] && (
                    <>
                        {
                            !formSectionRepeat && (
                                <BasicAlert
                                    title="No Records"
                                    description={`There has been no data recorded in OpenClinica for ${formSectionLabel}.`}
                                    severity="info"
                                    icon={faInfoCircle}
                                />
                            )
                        }
                        { 
                            !!formSectionConfiguration?.alertInformation && (
                                <BasicAlert
                                    description={<Markdown.Preview>{formSectionConfiguration.alertInformation}</Markdown.Preview>}
                                    severity="info"
                                    icon={faInfoCircle}
                                />
                            )
                        }
                        { 
                            !!formSectionConfiguration?.alertWarning && (
                                <BasicAlert
                                    description={<Markdown.Preview>{formSectionConfiguration.alertWarning}</Markdown.Preview>}
                                    severity="warning"
                                    icon={faExclamationCircle}
                                />
                            )
                        }
                        { 
                            !!formSectionConfiguration?.alertError && (
                                <BasicAlert
                                    description={<Markdown.Preview>{formSectionConfiguration.alertError}</Markdown.Preview>}
                                    severity="error"
                                    icon={faExclamationTriangle}
                                />
                            )
                        }
                        { 
                            !!formSectionConfiguration?.alertSuccess && (
                                <BasicAlert
                                    description={<Markdown.Preview>{formSectionConfiguration.alertSuccess}</Markdown.Preview>}
                                    severity="success"
                                    icon={faCheckCircle}
                                />
                            )
                        }
                    </>
                )
            }
            <Divider />
        </Content>
    )
}

export default SourceDataFormCollectionExpanding;